import {
  ClipboardList,
  FilePlus2,
  LayoutDashboard,
  List,
  ListPlus,
} from "lucide-react";

import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  Skeleton,
  useSidebar,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveMassTortQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { FileIcon, NoteIcon, TaskIcon } from "@/lib/utils/iconUtils";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { MatterDetailSubMenu } from "./MatterDetailSubMenu";

const ALL_MASS_TORTS_PATH = "/mass-torts";

const massTortSubNavLabelClass =
  "[&>span:last-child]:min-w-0 [&>span:last-child]:whitespace-nowrap [&>span:last-child]:overflow-hidden [&>span:last-child]:text-ellipsis";

export function MassTortsSubMenu({
  massTortSearchId,
  matterId,
  currentPath,
}: {
  massTortSearchId?: string;
  matterId?: string;
  currentPath: string;
}) {
  const { hasPagePermission } = useUser();
  const { setOpenMobile } = useSidebar();
  const isAllMassTortsActive =
    currentPath === ALL_MASS_TORTS_PATH ||
    currentPath === `${ALL_MASS_TORTS_PATH}/`;
  const massTortBasePath = massTortSearchId
    ? `/mass-torts/${massTortSearchId}`
    : "";
  const isMassTortOverviewActive =
    Boolean(massTortBasePath) &&
    (currentPath === massTortBasePath ||
      currentPath === `${massTortBasePath}/`);
  const canViewAllMassTorts = hasPagePermission(
    PAGE_PERMISSION_KEYS.massTortsAll,
  );
  const canViewOverview = hasPagePermission(
    PAGE_PERMISSION_KEYS.massTortsDetailOverview,
  );
  const canViewTasks = hasPagePermission(
    PAGE_PERMISSION_KEYS.massTortsDetailTasks,
  );
  const canViewSurveys = hasPagePermission(
    PAGE_PERMISSION_KEYS.massTortsDetailSurveys,
  );
  const canViewCustomFields = hasPagePermission(
    PAGE_PERMISSION_KEYS.massTortsDetailCustomFields,
  );
  const canViewNotes = hasPagePermission(
    PAGE_PERMISSION_KEYS.massTortsDetailNotes,
  );
  const canViewFileGeneration = hasPagePermission(
    PAGE_PERMISSION_KEYS.massTortsDetailFileGeneration,
  );
  const canViewFiles = hasPagePermission(
    PAGE_PERMISSION_KEYS.massTortsDetailFiles,
  );
  const canRetrieveMassTort =
    canViewOverview ||
    canViewTasks ||
    canViewSurveys ||
    canViewCustomFields ||
    canViewNotes ||
    canViewFileGeneration ||
    canViewFiles;
  const {
    data: massTortResponse,
    isLoading,
    isError,
  } = useQuery({
    ...retrieveMassTortQueryOptions(massTortSearchId ?? ""),
    enabled: Boolean(massTortSearchId) && canRetrieveMassTort,
  });
  const massTortTitle = massTortResponse?.data.title?.trim() || "Mass tort";

  return (
    <SidebarMenuSub className="mt-1.5 mr-0 ml-2 gap-1 border-l-2 border-l-neutral-900 py-0.5 pr-0 pl-3">
      {canViewAllMassTorts ? (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton
            asChild
            isActive={isAllMassTortsActive}
            className={massTortSubNavLabelClass}
          >
            <Link
              to={ALL_MASS_TORTS_PATH}
              onClick={() => {
                setOpenMobile(false);
              }}
            >
              <List className="mr-2 h-4 w-4 shrink-0" />
              All mass torts
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
      {massTortSearchId ? (
        <>
          {canViewOverview ? (
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                asChild
                isActive={isMassTortOverviewActive}
                className={massTortSubNavLabelClass}
              >
                <Link
                  to={massTortBasePath}
                  onClick={() => {
                    setOpenMobile(false);
                  }}
                >
                  {isLoading ? (
                    <div className="h-10 w-full py-1">
                      <Skeleton className="h-8 w-full rounded-md bg-neutral-900" />
                    </div>
                  ) : (
                    <>
                      <LayoutDashboard className="mr-2 h-4 w-4 shrink-0" />
                      <span className="min-w-0 truncate">
                        {isError ? "Error loading mass tort" : massTortTitle}
                      </span>
                    </>
                  )}
                </Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ) : matterId ? (
            <SidebarMenuSubItem>
              <SidebarMenuSubButton aria-disabled title={massTortTitle}>
                <LayoutDashboard className="mr-2 h-4 w-4 shrink-0" />
                <span className="min-w-0 truncate">{massTortTitle}</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ) : null}
          <SidebarMenuSubItem className="list-none p-0">
            <SidebarMenuSub className="mt-0.5 mr-0 ml-0 border-l-2 border-l-neutral-300 py-0.5 pr-0 pl-3">
              {matterId ? (
                <MatterDetailSubMenu
                  matterId={matterId}
                  currentPath={currentPath}
                />
              ) : (
                <>
                  {canViewTasks ? (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={currentPath === `${massTortBasePath}/tasks`}
                        className={massTortSubNavLabelClass}
                      >
                        <Link
                          to={`${massTortBasePath}/tasks`}
                          onClick={() => {
                            setOpenMobile(false);
                          }}
                        >
                          <TaskIcon className="mr-2 h-4 w-4 shrink-0" />
                          <span className="min-w-0">Tasks</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ) : null}
                  {canViewSurveys ? (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={currentPath === massTortBasePath + "/surveys"}
                        className={massTortSubNavLabelClass}
                      >
                        <Link
                          to={massTortBasePath + "/surveys"}
                          onClick={() => {
                            setOpenMobile(false);
                          }}
                        >
                          <ClipboardList className="mr-2 h-4 w-4 shrink-0" />
                          <span className="min-w-0">Surveys</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ) : null}
                  {canViewCustomFields ? (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={
                          currentPath === `${massTortBasePath}/custom-fields`
                        }
                        className={massTortSubNavLabelClass}
                      >
                        <Link
                          to={`${massTortBasePath}/custom-fields`}
                          onClick={() => {
                            setOpenMobile(false);
                          }}
                        >
                          <ListPlus className="mr-2 h-4 w-4 shrink-0" />
                          <span className="min-w-0">Custom fields</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ) : null}
                  {canViewNotes ? (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={currentPath === `${massTortBasePath}/notes`}
                        className={massTortSubNavLabelClass}
                      >
                        <Link
                          to={`${massTortBasePath}/notes`}
                          onClick={() => {
                            setOpenMobile(false);
                          }}
                        >
                          <NoteIcon className="mr-2 h-4 w-4 shrink-0" />
                          <span className="min-w-0">Notes</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ) : null}
                  {canViewFileGeneration ? (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={
                          currentPath === `${massTortBasePath}/file-generation`
                        }
                        className={massTortSubNavLabelClass}
                      >
                        <Link
                          to={`${massTortBasePath}/file-generation`}
                          onClick={() => {
                            setOpenMobile(false);
                          }}
                        >
                          <FilePlus2 className="mr-2 h-4 w-4 shrink-0" />
                          <span className="min-w-0">File generation</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ) : null}
                  {canViewFiles ? (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={currentPath === `${massTortBasePath}/files`}
                        className={massTortSubNavLabelClass}
                      >
                        <Link
                          to={`${massTortBasePath}/files`}
                          onClick={() => {
                            setOpenMobile(false);
                          }}
                        >
                          <FileIcon className="mr-2 h-4 w-4 shrink-0" />
                          <span className="min-w-0">Files</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ) : null}
                </>
              )}
            </SidebarMenuSub>
          </SidebarMenuSubItem>
        </>
      ) : null}
    </SidebarMenuSub>
  );
}
